import React, { useEffect, useState } from "react";
import eventBackground from "../assets/event.png";
import styles from "../styles/Events.module.css";
import { Calendar, MapPin } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import confetti from 'canvas-confetti';

const UpcomingEventPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [showForm, setShowForm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showFailurePopup, setShowFailurePopup] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [eventStarted, setEventStarted] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    fullName: "",
    phoneNumber: "",
    currentRoles: [],
    otherRole: "",
    startupStage: "",
    howHear: [],
    otherHowHear: "",
    hopesGain: [],
    otherHopesGain: "",
    futureTopics: "",
    willAttend: "",
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const eventDate = new Date("August 15, 2025 17:00:00 GMT+0300");

  const calculateTimeLeft = () => {
    const now = new Date();
    const difference = eventDate - now;

    if (difference <= 0) {
      setEventStarted(true);
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    let timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
    return timeLeft;
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    window.scrollTo(0, 0);

    const params = new URLSearchParams(location.search);
    if (params.get("openRegistration") === "true") {
      setShowForm(true);
    }
  }, [location]);

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft.days === 0 && newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0 && !eventStarted) {
        setEventStarted(true);
        triggerLaunchAnimation();
      }
    }, 1000);

    const initialTimeLeft = calculateTimeLeft();
    if (initialTimeLeft.days === 0 && initialTimeLeft.hours === 0 && initialTimeLeft.minutes === 0 && initialTimeLeft.seconds === 0) {
      setEventStarted(true);
    }

    return () => clearInterval(timer);
  }, [eventStarted]);

  const triggerLaunchAnimation = () => {
    console.log("Launching animation triggered!");
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });

    const countdownElement = document.getElementById('countdown-place');
    if (countdownElement) {
      countdownElement.classList.add(styles.launchAnimation);
      setTimeout(() => {
        countdownElement.classList.remove(styles.launchAnimation);
      }, 2000);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => {
        const currentSelection = prev[name];
        if (checked) {
          if (name === "currentRoles" || name === "howHear" || name === "hopesGain") {
            return { ...prev, [name]: [...currentSelection, value] };
          }
          return { ...prev, [name]: value };
        } else {
          if (name === "currentRoles" || name === "howHear" || name === "hopesGain") {
            return {
              ...prev,
              [name]: currentSelection.filter((item) => item !== value),
              ...(name === "currentRoles" && value === "Other" && { otherRole: "" }),
              ...(name === "howHear" && value === "Other" && { otherHowHear: "" }),
              ...(name === "hopesGain" && value === "Other" && { otherHopesGain: "" })
            };
          }
          return { ...prev, [name]: "" };
        }
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const sendToOdoo = async (data) => {
    console.log("Attempting to send data to Odoo:", data);
    try {
      const response = await fetch("https://inhouse.arifpay.org/api/Event/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "No error message from server." }));
        throw new Error(`HTTP error! Status: ${response.status}. Message: ${errorData.message || "Unknown error."}`);
      }

      const result = await response.json();
      console.log("Raw Odoo result:", result);

      let parsedResult;
      try {
        parsedResult = typeof result.result === "string" ? JSON.parse(result.result) : result.result;
      } catch (err) {
        throw new Error("Failed to parse result from server.");
      }

      if (!parsedResult.success) {
        throw new Error(parsedResult.error_data || "Registration failed. Please try again.");
      }

      return { success: true, data: parsedResult.return };
    } catch (error) {
      console.error("Error sending data to Odoo:", error);
      let userMessage = "An error occurred while submitting your registration. Please try again later.";
      if (error.message.includes("Failed to fetch")) {
        userMessage = "Unable to connect to the registration server. Please check your network or try again later.";
      }
      return { success: false, error: userMessage };
    }
  };

  const validateEthiopianPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, "");

    if (!/^(09|07)\d{8}$/.test(cleaned)) {
      return "Phone number must start with 09 or 07 and be 10 digits long (e.g., 0912345678).";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmissionError(null);
    setIsSubmitting(true);
    console.log("Submitting form");

    if (!formData.email || !formData.fullName || !formData.currentRoles.length || !formData.howHear.length || !formData.willAttend) {
      setSubmissionError("Please fill in all required fields.");
      setIsSubmitting(false);
      return;
    }
    console.log("Valid email and name");
    if (formData.phoneNumber && formData.phoneNumber.trim() !== "") {
      const phoneError = validateEthiopianPhone(formData.phoneNumber);
      if (phoneError) {
        setSubmissionError(phoneError);
        setIsSubmitting(false);
        return;
      }
    }

    console.log("Valid phone number");

    if (formData.currentRoles.includes("Other") && !formData.otherRole) {
      setSubmissionError("Please specify your role.");
      setIsSubmitting(false);
      return;
    }
    if (formData.currentRoles.includes("Startup Founder") && !formData.startupStage) {
      setSubmissionError("Please select your startup stage.");
      setIsSubmitting(false);
      return;
    }
    if (formData.howHear.includes("Other") && !formData.otherHowHear) {
      setSubmissionError("Please specify how you heard about the event.");
      setIsSubmitting(false);
      return;
    }
    if (formData.hopesGain.includes("Other") && !formData.otherHopesGain) {
      setSubmissionError("Please specify what you hope to gain.");
      setIsSubmitting(false);
      return;
    }

    const odooFormData = {
      attendee_name: formData.fullName,
      attendee_email: formData.email,
      attendee_phone: formData.phoneNumber,
      event_id: 1,
      attendee_answers: [
        { question_id: "1", answer: formData.currentRoles.includes("Other") ? `Other: ${formData.otherRole}` : formData.currentRoles.join(", ") },
        { question_id: "2", answer: formData.startupStage },
        { question_id: "3", answer: formData.howHear.includes("Other") ? `Other: ${formData.otherHowHear}` : formData.howHear.join(", ") },
        { question_id: "4", answer: formData.hopesGain.includes("Other") ? `Other: ${formData.otherHopesGain}` : formData.hopesGain.join(", ") },
        { question_id: "5", answer: formData.futureTopics },
        { question_id: "6", answer: formData.willAttend },
      ]
    };

    console.log("Sending to Odoo:", odooFormData);

    const odooResponse = await sendToOdoo(odooFormData);

    if (odooResponse.success) {
      setSubmittedEmail(formData.email);
      setIsSubmitted(true);
      setShowSuccessPopup(true);
      setSubmissionError(null);

      setFormData({
        email: "",
        fullName: "",
        phoneNumber: "",
        currentRoles: [],
        otherRole: "",
        startupStage: "",
        howHear: [],
        otherHowHear: "",
        hopesGain: [],
        otherHopesGain: "",
        futureTopics: "",
        willAttend: "",
      });

      setTimeout(() => {
        setShowSuccessPopup(false);
        setShowForm(false);
        setIsSubmitting(false);
        setSubmittedEmail("");
      }, 5000);
    } else {
      setSubmissionError(odooResponse.error);
      setIsSubmitting(false);
      setShowFailurePopup(true);
      setTimeout(() => {
        setShowFailurePopup(false);
      }, 5000);
    }
  };

  const showOtherRoleInput = formData.currentRoles.includes("Other");
  const showOtherHowHearInput = formData.howHear.includes("Other");
  const showOtherHopesGainInput = formData.hopesGain.includes("Other");
  const showStartupStageQuestion = formData.currentRoles.includes("Startup Founder");

  const navigateToDetails = () => {
    navigate("/Founders-Friday");
  };

  const handleRegisterNowClick = () => {
    if (!eventStarted) {
      // Assuming seats are full, show failure popup instead of opening form
      setShowFailurePopup(true);
      setTimeout(() => {
        setShowFailurePopup(false);
      }, 5000);
    }
  };

  return (
    <div>
      <Header />
      <div className={styles.container}>
        <div
          className={`${styles.upcomingEvent} ${isMobile ? styles.mobileUpcomingEvent : ""}`}
          style={{ backgroundImage: `url(${eventBackground})` }}
        >
          <div className={styles.eventContent}>
            <div className={styles.eventBadge}>UPCOMING EVENT</div>
            <h2 className={styles.eventTitle}>Founders' Friday</h2>
            <div className={styles.eventDetails}>
              <div className={styles.eventDetailItem}>
                <Calendar size={20} />
                <span>August 15, 2025</span>
              </div>
              <div className={styles.eventDetailItem}>
                <MapPin size={20} />
                <p style={{ fontSize: "18px" }}><span>weVentureHub HQ</span></p>
              </div>
              <div className={styles.eventDetailItem}>
                <div className={styles.eventTime}>5:00 PM</div>
              </div>
            </div>
            <p className={styles.eventDescription}>
              Objective: To enhance visibility, foster peer-to-peer knowledge
              transfer, and stimulate collaboration among startup founders and
              attendees coming from diverse backgrounds.
            </p>
            <div className={styles.eventActions}>
              <button
                className={styles.registerButton}
                onClick={handleRegisterNowClick}
                disabled={eventStarted}
                style={{ cursor: eventStarted ? "not-allowed" : "pointer" }}
              >
                {eventStarted ? "Registration Closed" : "Register Now"}
              </button>
              <button
                className={styles.detailsButton}
                onClick={navigateToDetails}
              >
                See Details
              </button>
            </div>
          </div>
          <div className={`${styles.eventVisual} ${isMobile ? styles.mobileEventVisual : ""}`}>
            <div className={styles.eventCard}>
              <div className={styles.eventCardHeader}>
                <div className={styles.eventCardDate}>15</div>
                <div className={styles.eventCardMonth}>AUG</div>
              </div>
              <div className={styles.eventCardBody}>
                <h3>Founders' Friday</h3>
                <p>weVentureHub HQ</p>
                <div className={styles.eventCardCountdown} id="countdown-place">
                  {eventStarted ? (
                    <div className={styles.eventLive}>
                      EVENT IS LIVE!
                    </div>
                  ) : (
                    <>
                      <div className={styles.countdownItem}>
                        <span>{timeLeft.days}</span>
                        <small>Days</small>
                      </div>
                      <div className={styles.countdownItem}>
                        <span>{timeLeft.hours}</span>
                        <small>Hours</small>
                      </div>
                      <div className={styles.countdownItem}>
                        <span>{timeLeft.minutes}</span>
                        <small>Min</small>
                      </div>
                      {!isMobile && (
                        <div className={styles.countdownItem}>
                          <span>{timeLeft.seconds}</span>
                          <small>Sec</small>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showForm && (
          <div className={styles.modalOverlay}>
            <div className={`${styles.modalContent} ${isMobile ? styles.mobileModalContent : ""}`}>
              <button
                className={styles.modalCloseButton}
                onClick={() => {
                  setShowForm(false);
                  setIsSubmitted(false);
                  setSubmissionError(null);
                  setIsSubmitting(false);
                  navigate("/Founders-Friday-Event");
                }}
              >
                &times;
              </button>

              {!showSuccessPopup ? (
                <>
                  <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                      RSVP Form: Founders' Friday & Fireside Chat Event
                    </h2>
                    <p className={styles.modalSubtitle}>
                      Join us for Founders' Friday & Fireside Chat, an event by weVenture and AriiPay to foster vital networking and knowledge sharing within the startup ecosystem.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className={styles.formContainer}>
                    {submissionError && (
                      <div className={styles.errorMessage}>
                        {submissionError}
                      </div>
                    )}

                    <div style={formFieldStyle}>
                      <label style={labelStyle}>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="Your email address"
                        style={inputTextStyle}
                      />
                    </div>
                    <br />

                    <div style={formFieldStyle}>
                      <label style={labelStyle}>Full name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        placeholder="Your answer"
                        style={inputTextStyle}
                      />
                    </div>
                    <br />

                    <div style={formFieldStyle}>
                      <label style={labelStyle}>Phone number</label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder="Your answer"
                        style={inputTextStyle}
                      />
                    </div>
                    <br />

                    <div style={formFieldStyle}>
                      <label style={labelStyle}>What best describes your current role/status? (Select all that apply) *</label>
                      <div style={checkboxGroupStyle}>
                        {[
                          "Startup Founder",
                          "Investor (Angel, VC, etc)",
                          "Mentor/Advisor",
                          "Industry Expert/Professional",
                          "Student",
                          "Government/Policy Maker",
                          "Other",
                        ].map((role) => (
                          <label key={role} style={checkboxLabelStyle}>
                            <input
                              type="checkbox"
                              name="currentRoles"
                              value={role}
                              checked={formData.currentRoles.includes(role)}
                              onChange={handleInputChange}
                              style={checkboxInputStyle}
                            />
                            <span>{role}</span>
                          </label>
                        ))}
                      </div>
                      {showOtherRoleInput && (
                        <input
                          type="text"
                          name="otherRole"
                          value={formData.otherRole}
                          onChange={handleInputChange}
                          required={showOtherRoleInput}
                          placeholder="Please specify your role"
                          style={{ ...inputTextStyle, marginTop: "15px" }}
                        />
                      )}
                    </div>
                    <br />

                    {showStartupStageQuestion && (
                      <div style={formFieldStyle}>
                        <label style={labelStyle}>If you are a startup founder/aspiring entrepreneur, please provide what stage your startup is currently in? *</label>
                        <div style={checkboxGroupStyle}>
                          {[
                            "Idea Stage/Brainstorming Ideas",
                            "Emergence stage, having prototype or MVP",
                            "Stability stage with paying customers",
                            "Growth stage with profitability and expanding market share",
                          ].map((stage) => (
                            <label key={stage} style={checkboxLabelStyle}>
                              <input
                                type="checkbox"
                                name="startupStage"
                                value={stage}
                                checked={formData.startupStage === stage}
                                onChange={handleInputChange}
                                style={checkboxInputStyle}
                              />
                              <span>{stage}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <br />

                    <div style={formFieldStyle}>
                      <label style={labelStyle}>How did you hear about this event? (Select all that apply) *</label>
                      <div style={checkboxGroupStyle}>
                        {[
                          "LinkedIn",
                          "Facebook",
                          "Telegram",
                          "Friend/Colleague",
                          "Other",
                        ].map((source) => (
                          <label key={source} style={checkboxLabelStyle}>
                            <input
                              type="checkbox"
                              name="howHear"
                              value={source}
                              checked={formData.howHear.includes(source)}
                              onChange={handleInputChange}
                              style={checkboxInputStyle}
                            />
                            <span>{source}</span>
                          </label>
                        ))}
                      </div>
                      {showOtherHowHearInput && (
                        <input
                          type="text"
                          name="otherHowHear"
                          value={formData.otherHowHear}
                          onChange={handleInputChange}
                          required={showOtherHowHearInput}
                          placeholder="Please specify how you heard"
                          style={{ ...inputTextStyle, marginTop: "15px" }}
                        />
                      )}
                    </div>

                    <br />

                    <div style={formFieldStyle}>
                      <label style={labelStyle}>What are you hoping to gain by attending this event? (Select all that apply) *</label>
                      <div style={checkboxGroupStyle}>
                        {[
                          "Networking with fellow entrepreneurs and professionals",
                          "Gaining insights into the Ethiopian startup ecosystem",
                          "Learning from experienced founders/presenters",
                          "Finding potential co-founders or team members",
                          "Showcasing my own startup/idea (informally)",
                          "Other",
                        ].map((gain) => (
                          <label key={gain} style={checkboxLabelStyle}>
                            <input
                              type="checkbox"
                              name="hopesGain"
                              value={gain}
                              checked={formData.hopesGain.includes(gain)}
                              onChange={handleInputChange}
                              style={checkboxInputStyle}
                            />
                            <span>{gain}</span>
                          </label>
                        ))}
                      </div>
                      {showOtherHopesGainInput && (
                        <input
                          type="text"
                          name="otherHopesGain"
                          value={formData.otherHopesGain}
                          onChange={handleInputChange}
                          required={showOtherHopesGainInput}
                          placeholder="Please specify what you hope to gain"
                          style={{ ...inputTextStyle, marginTop: "15px" }}
                        />
                      )}
                    </div>
                    <br />

                    <div style={formFieldStyle}>
                      <label style={labelStyle}>What topics related to startups, innovation, or financial technology would you be most interested in for future events?</label>
                      <textarea
                        name="futureTopics"
                        value={formData.futureTopics}
                        onChange={handleInputChange}
                        placeholder="Your answer"
                        rows="4"
                        style={{ ...inputTextStyle, resize: "vertical" }}
                      />
                    </div>
                    <br />

                    <div style={formFieldStyle}>
                      <label style={labelStyle}>Will you be able to attend the event? *</label>
                      <div style={checkboxGroupStyle}>
                        {[
                          "Yes, I will attend",
                          "No, I cannot attend",
                          "Maybe/Undecided",
                        ].map((attendance) => (
                          <label key={attendance} style={checkboxLabelStyle}>
                            <input
                              type="checkbox"
                              name="willAttend"
                              value={attendance}
                              checked={formData.willAttend === attendance}
                              onChange={handleInputChange}
                              style={checkboxInputStyle}
                            />
                            <span>{attendance}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    

                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "20px",
                      paddingBottom: "15px",
                      flexWrap: "wrap",
                      gap: "10px"
                    }}>
                      <button
                        type="button"
                        onClick={() => setFormData({
                          email: "",
                          fullName: "",
                          phoneNumber: "",
                          currentRoles: [],
                          otherRole: "",
                          startupStage: "",
                          howHear: [],
                          otherHowHear: "",
                          hopesGain: [],
                          otherHopesGain: "",
                          futureTopics: "",
                          willAttend: "",
                        })}
                        style={{
                          padding: "10px 20px",
                          backgroundColor: "#f0f0f0",
                          color: "#555",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "500",
                          transition: "background-color 0.2s",
                          flex: "1",
                          minWidth: "120px"
                        }}
                      >
                        Clear form
                      </button>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                          padding: "10px 20px",
                          backgroundColor: isSubmitting ? "#a0c8f8" : "#1a73e8",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: isSubmitting ? "not-allowed" : "pointer",
                          fontSize: "14px",
                          fontWeight: "500",
                          transition: "background-color 0.2s",
                          flex: "1",
                          minWidth: "120px"
                        }}
                      >
                        {isSubmitting ? "Submitting..." : "Submit"}
                      </button>
                    </div>
                  </form>
                </>
              ) : null}
            </div>
          </div>
        )}

        {showSuccessPopup && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}>
            <div style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "90%",
              textAlign: "center",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}>
              <div style={{
                fontSize: "48px",
                color: "#4CAF50",
                marginBottom: "20px",
              }}>✓</div>

              <h3 style={{ marginBottom: "15px", color: "#333" }}>
                Registration Successful!
              </h3>

              <p style={{ marginBottom: "20px", color: "#555" }}>
                Thank you for registering for Founders' Friday.
              </p>

              <p style={{ marginBottom: "25px", color: "#555" }}>
                A confirmation has been sent to <strong>{submittedEmail}</strong>
              </p>

              <button
                onClick={() => {
                  setShowSuccessPopup(false);
                  setShowForm(false);
                  setIsSubmitting(false);
                  setSubmittedEmail("");
                }}
                style={{
                  padding: "10px 25px",
                  backgroundColor: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "500",
                  transition: "all 0.3s",
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = "#3e8e41"}
                onMouseOut={(e) => e.target.style.backgroundColor = "#4CAF50"}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {showFailurePopup && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}>
            <div style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "8px",
              maxWidth: "500px",
              width: "90%",
              textAlign: "center",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              animation: "shake 0.3s ease-in-out",
            }}>
              <div style={{
                fontSize: "48px",
                color: "#f44336",
                marginBottom: "20px",
              }}>✕</div>

              <h3 style={{ marginBottom: "15px", color: "#333" }}>
                Registration Failed!
              </h3>

              <p style={{ marginBottom: "20px", color: "#555" }}>
                Sorry, the seat is full. See you on our next events.
              </p>

              <button
                onClick={() => setShowFailurePopup(false)}
                style={{
                  padding: "10px 25px",
                  backgroundColor: "#f44336",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "500",
                  transition: "all 0.3s",
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = "#d32f2f"}
                onMouseOut={(e) => e.target.style.backgroundColor = "#f44336"}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

const formFieldStyle = {
  backgroundColor: "#f8f8f8",
  borderRadius: "8px",
  padding: "15px",
  boxShadow: "inset 0 1px 3px rgba(0,0,0,0.05)"
};

const labelStyle = {
  marginBottom: "8px",
  fontWeight: "500",
  display: "block",
  color: "#333",
  fontSize: "14px"
};

const inputTextStyle = {
  width: "100%",
  padding: "10px",
  border: "1px solid #ddd",
  borderRadius: "4px",
  backgroundColor: "white",
  fontSize: "14px",
  outline: "none",
  color: "#555",
  boxSizing: "border-box"
};

const checkboxGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  marginTop: "8px"
};

const checkboxLabelStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: "10px",
  cursor: "pointer",
  fontSize: "14px",
  color: "#555",
  lineHeight: "1.4"
};

const checkboxInputStyle = {
  minWidth: "18px",
  minHeight: "18px",
  marginTop: "2px",
  accentColor: "#1a73e8",
  cursor: "pointer"
};

export default UpcomingEventPage;